import { useState, type CSSProperties, type FormEvent } from 'react';
import type { ChatClient, ChatState } from '../core/chat-client';

type SubmitStatus = 'idle' | 'sending' | 'done' | 'error';

/** Black or white text for a background colour, by perceived luminance. */
function readableText(hex: string): string {
  const value = hex.replace('#', '');
  const full =
    value.length === 3
      ? value
          .split('')
          .map((char) => char + char)
          .join('')
      : value;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return '#ffffff';
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? '#111111' : '#ffffff';
}

/**
 * The email-capture modal a SHOW_POPUP rule presents.
 *
 * Every line is founder-configured; on submit we store the email and swap to a
 * success state. Rendered above everything (`position: fixed`) but still inside
 * the widget's shadow root, so the host page's CSS can neither restyle nor
 * reach it.
 */
export function Popup({ client, state }: { client: ChatClient; state: ChatState }) {
  const popup = state.activePopup;
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<SubmitStatus>('idle');

  if (!popup) return null;
  const { config } = popup;

  const accentStyle: CSSProperties | undefined = config.accentColor
    ? ({
        '--ic-popup-accent': config.accentColor,
        '--ic-popup-accent-fg': readableText(config.accentColor),
      } as CSSProperties)
    : undefined;

  async function submit(event: FormEvent) {
    event.preventDefault();
    const value = email.trim();
    if (!value || status === 'sending') return;

    setStatus('sending');
    try {
      await client.submitPopupEmail(value);
      setStatus('done');
    } catch {
      setStatus('error');
    }
  }

  return (
    <div className="popup-overlay" onClick={() => client.dismissPopup()}>
      <div
        className={`popup-card${config.imageUrl ? ' has-image' : ''}`}
        data-theme={config.theme}
        role="dialog"
        aria-modal="true"
        aria-label={config.title}
        style={accentStyle}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="popup-close"
          aria-label="Close"
          onClick={() => client.dismissPopup()}
        >
          ×
        </button>

        {config.imageUrl && <img className="popup-image" src={config.imageUrl} alt="" />}

        {status === 'done' ? (
          <div className="popup-success">
            <span className="popup-check" aria-hidden>
              ✓
            </span>
            <p className="popup-success-text">{config.successMessage}</p>
            <button type="button" className="popup-submit" onClick={() => client.dismissPopup()}>
              Done
            </button>
          </div>
        ) : (
          <>
            <h2 className="popup-title">{config.title}</h2>
            {config.description && <p className="popup-desc">{config.description}</p>}
            <form className="popup-form" onSubmit={submit}>
              <input
                className="popup-input"
                type="email"
                required
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                placeholder={config.emailPlaceholder}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                aria-label="Email address"
              />
              <button type="submit" className="popup-submit" disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending…' : config.submitLabel}
              </button>
            </form>
            {status === 'error' && (
              <p className="popup-error">Something went wrong — please try again.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
